import { saveAs } from "file-saver";
import { bodycamClient } from "./clients";

export type TranscriptDownloadFormat = "txt" | "docx" | "pdf";

const CONTENT_TYPE_BY_FORMAT: Record<TranscriptDownloadFormat, string> = {
  txt: "text/plain;charset=utf-8",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  pdf: "application/pdf",
};

const EXTENSION_BY_FORMAT: Record<TranscriptDownloadFormat, string> = {
  txt: "txt",
  docx: "docx",
  pdf: "pdf",
};

/**
 * Extracts the download filename from a `Content-Disposition` header, preferring
 * the RFC 5987 `filename*` form. Returns null when the header is absent or has no
 * filename, so callers can fall back to a locally-derived name.
 */
const filenameFromContentDisposition = (
  header: string | undefined,
): string | null => {
  if (!header) return null;
  const extended = header.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
  if (extended?.[1]) {
    try {
      return decodeURIComponent(extended[1].trim().replace(/^["']|["']$/g, ""));
    } catch {
      /* malformed encoding → fall through to the basic form */
    }
  }
  const basic = header.match(/filename="?([^";]+)"?/i);
  return basic?.[1]?.trim() ?? null;
};

/**
 * Downloads a single transcript rendered server-side (bodycam/evidence service)
 * and hands the file to the browser via file-saver. The `native` language
 * sentinel is passed through as-is; the server skips translation for it. The
 * server owns the canonical formatting and filename (via `Content-Disposition`);
 * we fall back to `transcript-{evidenceId}-{languageCode}.{ext}` if it is absent.
 * Throws on failure — callers own their toast/error handling.
 */
export async function downloadTranscript(opts: {
  evidenceId: string;
  format: TranscriptDownloadFormat;
  languageCode: string;
  /** Saved version to render; omitted → server head/latest. */
  version?: number;
}): Promise<void> {
  const { evidenceId, format, languageCode, version } = opts;
  const response = await bodycamClient.get(
    `/transcripts/${encodeURIComponent(evidenceId)}/download`,
    {
      params: {
        format,
        languageCode,
        ...(version != null ? { version } : {}),
      },
      responseType: "blob",
      headers: { Accept: CONTENT_TYPE_BY_FORMAT[format] },
    },
  );
  const fallback = `transcript-${evidenceId}-${languageCode}.${EXTENSION_BY_FORMAT[format]}`;
  const filename =
    filenameFromContentDisposition(response.headers?.["content-disposition"]) ??
    fallback;
  saveAs(
    new Blob([response.data], { type: CONTENT_TYPE_BY_FORMAT[format] }),
    filename,
  );
}

/**
 * Downloads a combined, multi-video transcript (TXT) rendered server-side.
 * Order of `evidenceIds` is preserved in the output. The client owns the
 * filename here (report-scoped), so `Content-Disposition` is not consulted.
 * Throws on failure — callers own their toast/error handling.
 */
export async function downloadCombinedTranscript(opts: {
  evidenceIds: string[];
  languageCode: string;
  filenameBase: string;
}): Promise<void> {
  const { evidenceIds, languageCode, filenameBase } = opts;
  const response = await bodycamClient.post(
    `/transcripts/download/combined`,
    { evidenceIds, languageCode, format: "txt" },
    { responseType: "blob", headers: { Accept: "text/plain" } },
  );
  saveAs(
    new Blob([response.data], { type: "text/plain;charset=utf-8" }),
    `${filenameBase}-${languageCode}.txt`,
  );
}
